import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
    Users,
    FolderKanban,
    Globe2,
    RefreshCw,
    Mail,
    Activity
} from "lucide-react";
import {
    getAccounts,
    getCrmProjects,
    getLeads
} from "../../services/crmService";

import BillingUsageCard from "../../components/billing/BillingUsageCard";

function AgencyMetric({ icon: Icon, label, value, hint, to }) {
    const content = (
        <>
            <div className="dash-metric-icon">
                <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value || 0}</strong>
            <small>{hint}</small>
        </>
    );

    if (to) {
        return (
            <Link to={to} className="dash-metric-card clickable">
                {content}
            </Link>
        );
    }

    return <div className="dash-metric-card">{content}</div>;
}

function AgencySection({ kicker, title, description, action, children }) {
    return (
        <section className="dash-panel">
            <div className="dash-panel-head">
                <div>
                    <span className="project-kicker">{kicker}</span>
                    <h3>{title}</h3>
                    {description && <p>{description}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function EmptyBlock({ children }) {
    return <div className="dashboard-empty refined">{children}</div>;
}

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

export default function AgencyDashboard() {
    const [accounts, setAccounts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const [accountRes, projectRes, leadRes] = await Promise.allSettled([
                getAccounts(),
                getCrmProjects(),
                getLeads()
            ]);

            if (accountRes.status === "fulfilled") {
                setAccounts(Array.isArray(accountRes.value) ? accountRes.value : []);
            } else {
                console.error("Agency accounts failed", accountRes.reason);
            }

            if (projectRes.status === "fulfilled") {
                setProjects(Array.isArray(projectRes.value) ? projectRes.value : []);
            } else {
                console.error("Agency projects failed", projectRes.reason);
            }

            if (leadRes.status === "fulfilled") {
                setLeads(Array.isArray(leadRes.value) ? leadRes.value : []);
            } else {
                console.error("Agency leads failed", leadRes.reason);
            }

            const failed = [accountRes, projectRes, leadRes].some(x => x.status === "rejected");

            if (failed) {
                setError("Some agency dashboard data could not be loaded.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const summary = useMemo(() => {
        const agencyAccounts = accounts.filter(x => x.accountType === "Agency");
        const clients = accounts.filter(x => x.accountType === "Client");
        const published = projects.filter(x => x.liveUrl).length;
        const newLeads = leads.filter(x => String(x.status || "").toLowerCase() === "new").length;

        return {
            agencyAccounts: agencyAccounts.length,
            clients: clients.length,
            projects: projects.length,
            published,
            leads: leads.length,
            newLeads
        };
    }, [accounts, projects, leads]);

    const recentProjects = useMemo(() => {
        return [...projects]
            .sort((a, b) => new Date(b.createdAtUtc || 0) - new Date(a.createdAtUtc || 0))
            .slice(0, 6);
    }, [projects]);

    const recentLeads = useMemo(() => {
        return [...leads]
            .sort((a, b) => new Date(b.createdAtUtc || 0) - new Date(a.createdAtUtc || 0))
            .slice(0, 6);
    }, [leads]);

    return (
        <div className="dash-shell">
            <section className="dash-hero-card">
                <div>
                    <span className="project-kicker">Agency Command Center</span>
                    <h2>Client Growth Workspace</h2>
                    <p>
                        Manage your agency clients, generated websites, leads, and live site operations from one workspace.
                    </p>
                </div>

                <button
                    type="button"
                    className="project-action-btn secondary"
                    onClick={load}
                    disabled={loading}
                >
                    <RefreshCw size={16} />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </section>

            <section className="dash-metric-grid">
                <AgencyMetric
                    icon={Users}
                    label="Clients"
                    value={summary.clients}
                    hint="Assigned client accounts"
                    to="/accounts"
                />

                <AgencyMetric
                    icon={FolderKanban}
                    label="Projects"
                    value={summary.projects}
                    hint="Client websites"
                    to="/projects"
                />

                <AgencyMetric
                    icon={Globe2}
                    label="Published"
                    value={summary.published}
                    hint="Live client websites"
                    to="/projects"
                />

                <AgencyMetric
                    icon={Mail}
                    label="New Leads"
                    value={summary.newLeads}
                    hint={`${summary.leads} total leads`}
                    to="/leads"
                />
            </section>

            {error && (
                <div className="dash-alert error">
                    <strong>Attention needed</strong>
                    <p>{error}</p>
                </div>
            )}

            <BillingUsageCard />

            <section className="dash-admin-grid">
                <AgencySection
                    kicker="Client Portfolio"
                    title="Recent Client Projects"
                    description="Open a client website workspace to update CRM content, publish changes, or review leads."
                    action={
                        <Link className="project-action-btn secondary" to="/projects">
                            View Projects
                        </Link>
                    }
                >
                    {recentProjects.length === 0 ? (
                        <EmptyBlock>No client projects assigned yet.</EmptyBlock>
                    ) : (
                        <div className="dash-quality-list">
                            {recentProjects.map((project) => (
                                <Link
                                    key={project.projectId || project.id}
                                    className="dash-quality-item clickable-row"
                                    to={`/crm/projects/${project.projectId || project.id}`}
                                >
                                    <div>
                                        <strong>{project.businessName || "Untitled Project"}</strong>
                                        <p>
                                            {project.industry || "Website"} · {project.status || "Draft"}
                                        </p>
                                    </div>

                                    <div className="dash-quality-stats">
                                        <span>{project.liveUrl ? "Published" : "Not Published"}</span>
                                        <span>{formatDate(project.createdAtUtc)}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </AgencySection>

                <AgencySection
                    kicker="Pipeline"
                    title="Recent Leads"
                    description="Latest enquiries across your agency client websites."
                    action={
                        <Link className="project-action-btn secondary" to="/leads">
                            View Leads
                        </Link>
                    }
                >
                    {recentLeads.length === 0 ? (
                        <EmptyBlock>No leads captured yet.</EmptyBlock>
                    ) : (
                        <div className="dash-signal-list">
                            {recentLeads.map((lead) => (
                                <div key={lead.id} className="dash-signal-item">
                                    <div className="dash-signal-count">
                                        <Activity size={16} />
                                    </div>

                                    <div>
                                        <strong>{lead.fullName || "Unnamed Lead"}</strong>
                                        <span>{lead.email || lead.phone || "No contact detail"}</span>
                                        <small>{lead.source || "website-form"} · {lead.status || "New"}</small>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </AgencySection>
            </section>

            <AgencySection
                kicker="Quick Actions"
                title="Agency Operations"
                description="Common actions for managing clients and client websites."
            >
                <div className="dash-agency-actions">
                    <Link className="project-action-btn primary" to="/accounts">
                        Create / Manage Clients
                    </Link>

                    <Link className="project-action-btn secondary" to="/projects">
                        Open Client Projects
                    </Link>

                    <Link className="project-action-btn secondary" to="/leads">
                        Review Leads
                    </Link>

                    <Link className="project-action-btn secondary" to="/settings">
                        Agency Settings
                    </Link>
                </div>
            </AgencySection>
        </div>
    );
}